import React, { memo, useCallback, useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, StatusBar, TouchableOpacity, FlatList, Platform } from 'react-native';
import BackIcon from '../assets/images/back.svg';
import { COLORS, ROUTES } from '../constants';
import { Icon } from '../lib/Icon';
import { NavigationProps } from './Home';
import { HymnProps, ItemProps } from '../types';
import { useGlobalContext } from '../context/GlobalContext';
import RemoveIcon from '../assets/images/remove.svg';
import { openDatabase } from '../database/database';

const Item = memo(({item, onPress, selected, authors, onDelete}: ItemProps) => {
  return (
    <View style={[stylesItem.container]}>
      <TouchableOpacity onPress={onPress} style={[stylesItem.item]}>
        <View style={stylesItem.avatar}>
          <Text style={stylesItem.number}>{item.number}</Text>
        </View>
        <View style={{ flex: 1 }}>
          <Text style={[
            stylesItem.title,
            { color: selected ? COLORS.warning : COLORS.text }
          ]}>{item.title}</Text>
          <Text style={stylesItem.author}>{item.author?.text}</Text>
        </View>
      </TouchableOpacity>
      <TouchableOpacity onPress={onDelete} style={stylesItem.buttonRight}>
        <Icon><RemoveIcon /></Icon>
      </TouchableOpacity>
    </View>
  )
})

const stylesItem = StyleSheet.create({
  container: {
    marginBottom: 5,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    borderRadius: 5
  },
  item: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  avatar: {
    borderWidth: 1,
    borderColor: COLORS.borderLight,
    borderRadius: 50,
    height: 52,
    width: 52,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  number: {
    fontFamily: 'PoppinsBold',
    fontSize: 22,
    color: COLORS.primary
  },
  author: {
    fontSize: 13,
    fontFamily: 'Poppins',
    color: COLORS.textLight,
    marginTop: -5,
  },
  title: {
    fontSize: 18,
    fontFamily: 'PoppinsBold',
    lineHeight: 24
  },
  buttonRight: {
    padding: 20
  }
});

function Favorites (props: NavigationProps) {
  const { setHymnId, authors, favorites, setFavorites } = useGlobalContext();
  const [selectedId, setSelectedId] = useState<number>();

  const renderItem = ({item}: {item: HymnProps}) => {
    const selected = item.number === selectedId;

    const selectItem = () => {
      setSelectedId(item.number);
      setHymnId(item.number);
      props.navigation?.navigate(ROUTES.HOME_DRAWER);
    }
    
    const onDelete = async () => {
      const items: HymnProps[] = favorites.filter(f => f.id !== item.id);
      if (Platform.OS === 'web') {
        window.localStorage.setItem('favorites', JSON.stringify(items));
      } else {
        const db = await openDatabase();
        db.transaction((tx) => {
          tx.executeSql(
            'DELETE from favorites WHERE hymn_id=?;',
            [item.id]
          );
        });
      }
      setFavorites(items);
    }

    return (
      <Item
        item={item}
        onPress={selectItem}
        onDelete={onDelete}
        selected={selected}
        authors={authors}
      />
    );
  };

  const closeSearch = () => {
    props.navigation?.goBack();
  }

  const ITEM_HEIGHT = 72;
  const keyExtractor = useCallback((item: HymnProps) => String(item.id), []);
  const getItemLayout = useCallback((_: any, index: number) => ({length: ITEM_HEIGHT, offset: ITEM_HEIGHT * index, index}), []);
  
  return (
    <SafeAreaView style={styles.container}>
      <StatusBar backgroundColor={COLORS.primary} />
      <View style={stylesHeader.container}>
        <View style={stylesHeader.containerButton}>
          <TouchableOpacity
            style={stylesHeader.buttonLeft}
            onPress={closeSearch}>
            <Icon><BackIcon /></Icon>
          </TouchableOpacity>
        </View>
        <View style={stylesHeader.containerLabel}>
          <Text style={stylesHeader.label}>Mis favoritos</Text>
        </View>
      </View>
      <View style={styles.body}>
        {favorites.length > 0 && <FlatList
          data={favorites.sort((a, b) => a.id - b.id)}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          extraData={selectedId}
          getItemLayout={getItemLayout}
          initialNumToRender={ 10 }
          windowSize={ 10 } 
          maxToRenderPerBatch={ 10 }
        />}
        {favorites.length === 0 && <Text style={styles.filterEmpty}>
          No tiene ningún favorito seleccionado.
        </Text>}
      </View>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: COLORS.primary,
    flex: 1,
    position: 'relative',
  },
  body: {
    flex: 1,
    backgroundColor: COLORS.background,
    padding: 8 
  },
  filterEmpty: {
    textAlign: 'center',
    fontFamily: 'Poppins',
    marginTop: 10
  }
});

const stylesHeader = StyleSheet.create({
  container: {
    backgroundColor: COLORS.primary,
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: 50
  },
  containerButton: {
    width: 52
  },
  buttonLeft: {
    padding: 10,
    marginTop: 5,
    marginHorizontal: 5,
    height: 40
  },
  containerLabel: {
    flex: 1,
    justifyContent: 'center',
  },
  label: {
    fontFamily: 'PoppinsBold',
    color: COLORS.white,
    fontSize: 18,
  },
});

export default Favorites;
